/**
 * Account modal (sc-8410) — the self-serve half of our privacy promises.
 *
 * The privacy policy tells players they can access, export and delete
 * their data. Until this shipped there was no way to do any of it from
 * the cabinet: a request meant emailing us and an operator running SQL
 * by hand. This is that promise, made real and self-serve.
 *
 * Two actions, deliberately asymmetric in how hard they are to trigger:
 *
 *   - EXPORT is one click. It is non-destructive, so friction would be
 *     pure cost.
 *   - DELETE requires typing the word DELETE. It erases the account
 *     everywhere — local rows, the Zyphr identity, and any live PayPal
 *     subscription — and none of it comes back. A confirm button alone
 *     is too easy to hit on a phone, next to LOGOUT, by accident.
 *
 * Reuses the `.login-modal` CSS block so this matches the sign-in and
 * install overlays rather than introducing a third modal look.
 */

import { deleteMyAccount, exportMyData } from '../api/client.js';
import { clearSession, getSession } from '@arcraz/arcadia-auth-web';
import { resetAnalytics, track } from '../runtime/analytics.js';

/** Only one modal at a time; a second call returns the open one. */
let openModal: { close: () => void } | null = null;

/** The word a player must type to confirm deletion. */
const CONFIRM_WORD = 'DELETE';

export function openAccountModal(): { close: () => void } {
  if (openModal) return openModal;

  const previouslyFocused = document.activeElement as HTMLElement | null;
  const session = getSession();

  const root = document.createElement('div');
  root.className = 'login-modal';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-labelledby', 'account-modal-title');

  const card = document.createElement('div');
  card.className = 'login-modal__card';

  const title = document.createElement('h2');
  title.id = 'account-modal-title';
  title.className = 'login-modal__title';
  title.textContent = 'YOUR ACCOUNT';

  const body = document.createElement('p');
  body.className = 'login-modal__body';
  body.textContent = session
    ? `Signed in as ${session.email}.`
    : 'Signed in.';

  const status = document.createElement('p');
  status.className = 'login-modal__error';
  status.setAttribute('role', 'alert');

  // --- Export -------------------------------------------------------
  const exportBtn = document.createElement('button');
  exportBtn.type = 'button';
  exportBtn.className = 'login-modal__btn';
  exportBtn.textContent = 'EXPORT MY DATA';

  // --- Delete -------------------------------------------------------
  const deleteBtn = document.createElement('button');
  deleteBtn.type = 'button';
  deleteBtn.className = 'login-modal__btn login-modal__btn--danger';
  deleteBtn.textContent = 'DELETE MY ACCOUNT';

  const confirmWrap = document.createElement('div');
  confirmWrap.hidden = true;

  const confirmCopy = document.createElement('p');
  confirmCopy.className = 'login-modal__body';
  confirmCopy.textContent =
    `This deletes your scores, purchases and sign-in for good, and cancels ` +
    `any active pass. It cannot be undone. Type ${CONFIRM_WORD} to confirm.`;

  const confirmInput = document.createElement('input');
  confirmInput.type = 'text';
  confirmInput.className = 'login-modal__input';
  confirmInput.placeholder = CONFIRM_WORD;
  confirmInput.autocomplete = 'off';
  confirmInput.setAttribute('aria-label', `Type ${CONFIRM_WORD} to confirm`);

  const confirmBtn = document.createElement('button');
  confirmBtn.type = 'button';
  confirmBtn.className = 'login-modal__btn login-modal__btn--danger';
  confirmBtn.textContent = 'PERMANENTLY DELETE';
  confirmBtn.disabled = true;

  confirmWrap.append(confirmCopy, confirmInput, confirmBtn);

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = 'login-modal__btn';
  closeButton.textContent = 'CLOSE';

  const actions = document.createElement('div');
  actions.className = 'login-modal__actions';
  actions.append(exportBtn, deleteBtn, closeButton);

  card.append(title, body, status, actions, confirmWrap);
  root.appendChild(card);
  document.body.appendChild(root);

  setTimeout(() => closeButton.focus(), 0);

  const close = (): void => {
    document.removeEventListener('keydown', onKeydown);
    root.remove();
    openModal = null;
    previouslyFocused?.focus?.();
  };

  function onKeydown(ev: KeyboardEvent): void {
    if (ev.key === 'Escape') close();
  }
  document.addEventListener('keydown', onKeydown);

  // Backdrop click closes; clicks inside the card must not.
  root.addEventListener('click', (ev) => {
    if (ev.target === root) close();
  });

  closeButton.addEventListener('click', close);

  // --- Export handler -----------------------------------------------
  exportBtn.addEventListener('click', () => {
    void (async () => {
      status.textContent = '';
      exportBtn.disabled = true;
      exportBtn.textContent = 'PREPARING…';
      try {
        const data = await exportMyData();
        downloadJson(data);
        track('account_data_exported');
        status.textContent = 'Download started.';
      } catch (err) {
        status.textContent = errorText(err, 'Could not export your data.');
      } finally {
        exportBtn.disabled = false;
        exportBtn.textContent = 'EXPORT MY DATA';
      }
    })();
  });

  // --- Delete handlers ----------------------------------------------
  deleteBtn.addEventListener('click', () => {
    confirmWrap.hidden = false;
    deleteBtn.disabled = true;
    confirmInput.focus();
  });

  // Case-insensitive so a phone's autocapitalise doesn't lock someone
  // out of a right they are entitled to exercise.
  confirmInput.addEventListener('input', () => {
    confirmBtn.disabled =
      confirmInput.value.trim().toUpperCase() !== CONFIRM_WORD;
  });

  confirmBtn.addEventListener('click', () => {
    void (async () => {
      status.textContent = '';
      confirmBtn.disabled = true;
      confirmInput.disabled = true;
      confirmBtn.textContent = 'DELETING…';
      try {
        await deleteMyAccount();
        // The identity this session's tokens point at no longer exists,
        // so the local session must go too — otherwise the cabinet keeps
        // acting signed-in until the next refresh fails.
        track('account_deleted');
        clearSession();
        resetAnalytics();
        close();
      } catch (err) {
        // Most likely the fail-closed PayPal path: nothing was deleted,
        // so the account is intact and the player can retry.
        status.textContent = errorText(
          err,
          'Could not delete your account. Nothing was changed.'
        );
        confirmBtn.disabled = false;
        confirmInput.disabled = false;
        confirmBtn.textContent = 'PERMANENTLY DELETE';
      }
    })();
  });

  openModal = { close };
  return openModal;
}

/** Hand the export to the browser as a downloaded .json file. */
function downloadJson(data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = 'arcadia84-my-data.json';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick — revoking synchronously can cancel the
  // download in some browsers before it has read the blob.
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}
